import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import WelcomeTour from "@/components/WelcomeTour";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Zap, 
  Users, 
  FileBarChart, 
  TrendingUp,
  Play,
  ExternalLink,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Building2,
  Terminal,
  X,
  Info,
  AlertTriangle,
  ArrowRight,
  Copy,
  Check
} from "lucide-react";

const statusLabels = {
  pending: "Pendiente",
  scraping: "Scrapeando",
  analyzing: "Analizando",
  completed: "Completado",
  failed: "Fallido",
  timeout: "Timeout"
};

const getStatusBadgeClass = (status) => {
  if (status === "completed") return "bg-emerald-500/10 text-emerald-400";
  if (status === "failed" || status === "timeout") return "bg-red-500/10 text-red-400";
  return "bg-cyan-500/10 text-cyan-400";
};

const getStatusIcon = (status) => {
  if (status === "completed") return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
  if (status === "failed" || status === "timeout") return <AlertCircle className="w-5 h-5 text-red-400" />;
  return <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />;
};

const PROGRESS_BY_STATUS = { pending: 10, scraping: 40, analyzing: 70 };

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [quota, setQuota] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  
  // Selection dialog state
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  
  // Logs dialog state
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsStatus, setLogsStatus] = useState("");
  const [logsCopied, setLogsCopied] = useState(false);
  const logsEndRef = useRef(null);
  const logsViewportRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, analysesRes, competitorsRes, quotaRes] = await Promise.all([
        apiClient.get("/stats"),
        apiClient.get("/analyses"),
        apiClient.get("/competitors"),
        apiClient.get("/me/quota").catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setRecentAnalyses(analysesRes.data.slice(0, 5));
      setCompetitors(competitorsRes.data.filter(c => c.active));
      setQuota(quotaRes.data);
    } catch (error) {
      // Silent: dashboard polls periodically — transient failures are non-blocking
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    if (!currentAnalysisId) return;
    try {
      const response = await apiClient.get(`/analyses/${currentAnalysisId}/logs`);
      setLogs(response.data.logs || []);
      setLogsStatus(response.data.status);
      
      // Close dialog if analysis is complete
      if (["completed", "failed", "timeout"].includes(response.data.status)) {
        loadData();
      }
    } catch (error) {
      // Silent: polls periodically, transient failures are non-blocking
    }
  }, [currentAnalysisId, loadData]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [loadData]);
  
  // Poll logs ONLY while analysis is running. Stop polling once terminal status reached.
  useEffect(() => {
    if (!logsDialogOpen || !currentAnalysisId) return;
    loadLogs();
    // Don't keep polling if status is already terminal
    if (["completed", "failed", "timeout"].includes(logsStatus)) return;
    const interval = setInterval(loadLogs, 3000);
    return () => clearInterval(interval);
  }, [logsDialogOpen, currentAnalysisId, logsStatus, loadLogs]);
  
  // Reset stick-to-bottom when dialog opens
  useEffect(() => {
    if (logsDialogOpen) {
      stickToBottomRef.current = true;
      setLogsCopied(false);
    }
  }, [logsDialogOpen]);
  
  // Attach scroll listener to the Radix ScrollArea viewport to track if user scrolled up
  useEffect(() => {
    if (!logsDialogOpen) return;
    // Radix ScrollArea exposes the viewport via [data-radix-scroll-area-viewport]
    const viewport = document.querySelector('[data-logs-scroll-area] [data-radix-scroll-area-viewport]');
    if (!viewport) return;
    logsViewportRef.current = viewport;
    const handleScroll = () => {
      const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      // If user is within 40px of the bottom, keep auto-scrolling. Otherwise, respect their position.
      stickToBottomRef.current = distanceFromBottom < 40;
    };
    viewport.addEventListener('scroll', handleScroll);
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [logsDialogOpen]);
  
  // Auto-scroll logs ONLY when user is already at the bottom (stick-to-bottom pattern)
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [logs]);
  
  const copyLogsToClipboard = async () => {
    if (!logs.length) return;
    const lines = logs.map(log => {
      const ts = log.timestamp ? new Date(log.timestamp).toLocaleTimeString("es-ES", { hour12: false }) : "";
      const lvl = (log.level || "info").toUpperCase();
      let line = `[${ts}] [${lvl}] ${log.message || ""}`;
      if (log.details) {
        try {
          line += `\n${JSON.stringify(log.details, null, 2)}`;
        } catch {
          line += `\n${String(log.details)}`;
        }
      }
      return line;
    });
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setLogsCopied(true);
      setTimeout(() => setLogsCopied(false), 2000);
    } catch (err) {
      // Clipboard API may be unavailable (e.g. non-HTTPS contexts) — fall back silently
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setLogsCopied(true);
        setTimeout(() => setLogsCopied(false), 2000);
      } catch (e) {
        toast.error("No se pudo copiar al portapapeles");
      }
      document.body.removeChild(ta);
    }
  };

  const openSelectionDialog = () => {
    setSelectedCompetitors(competitors.map(c => c.id));
    setSelectDialogOpen(true);
  };

  const toggleCompetitor = (competitorId) => {
    setSelectedCompetitors(prev => 
      prev.includes(competitorId)
        ? prev.filter(id => id !== competitorId)
        : [...prev, competitorId]
    );
  };

  const toggleAll = () => {
    if (selectedCompetitors.length === competitors.length) {
      setSelectedCompetitors([]);
    } else {
      setSelectedCompetitors(competitors.map(c => c.id));
    }
  };

  const launchAnalysis = async () => {
    if (selectedCompetitors.length === 0) {
      toast.error("Selecciona al menos un competidor");
      return;
    }
    
    setLaunching(true);
    setSelectDialogOpen(false);
    
    try {
      const response = await apiClient.post("/analyses/launch", {
        competitor_ids: selectedCompetitors
      });
      toast.success(`Análisis iniciado con ${selectedCompetitors.length} competidor(es)`);
      
      // Open logs dialog
      setCurrentAnalysisId(response.data.analysis_id);
      setLogs([]);
      setLogsDialogOpen(true);
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al lanzar análisis");
    } finally {
      setLaunching(false);
    }
  };
  
  const openLogsDialog = (analysisId) => {
    setCurrentAnalysisId(analysisId);
    setLogs([]);
    setLogsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <WelcomeTour />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight font-['Outfit']">
            Dashboard
          </h1>
          <p className="text-slate-400 mt-1">
            Bienvenido de vuelta, {user?.name?.split(" ")[0]}
          </p>
        </div>
        <Button
          onClick={openSelectionDialog}
          disabled={launching || competitors.length === 0 || !user?.drive_connected}
          className="bg-cyan-600 hover:bg-cyan-500 text-white btn-glow"
          data-testid="launch-analysis-btn"
        >
          {launching ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Play className="w-5 h-5 mr-2" />
          )}
          Lanzar Análisis
        </Button>
      </div>

      {/* Drive-not-connected banner */}
      {user && !user.drive_connected && (
        <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="drive-required-banner">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-amber-100 font-medium">
              Conecta tu Google Drive antes de lanzar un análisis
            </p>
            <p className="text-xs text-amber-200/70 mt-0.5">
              Los informes y los creativos descargados se guardan en tu Drive. Sin esta conexión no podrás iniciar análisis.
            </p>
          </div>
          <a
            href="/settings"
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-sm font-medium transition-colors"
            data-testid="connect-drive-cta"
          >
            Conectar Drive <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}

      {/* Selection Dialog */}
      <Dialog open={selectDialogOpen} onOpenChange={setSelectDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Seleccionar Competidores
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Elige los competidores que quieres incluir en este análisis
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-2">
            <div 
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-950/50 border border-white/5 cursor-pointer hover:border-cyan-500/30 transition-colors"
              onClick={toggleAll}
            >
              <Checkbox 
                checked={selectedCompetitors.length === competitors.length && competitors.length > 0}
                className="border-slate-600 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
              />
              <span className="font-medium text-cyan-400">
                {selectedCompetitors.length === competitors.length ? "Deseleccionar todos" : "Seleccionar todos"}
              </span>
              <span className="ml-auto text-sm text-slate-500">
                {selectedCompetitors.length} / {competitors.length}
              </span>
            </div>

            <div className="border-t border-white/5 my-3"></div>

            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
              {competitors.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No hay competidores activos</p>
                  <p className="text-xs mt-1">Agrega competidores en la sección Competidores</p>
                </div>
              ) : (
                competitors.map((competitor) => (
                  <div 
                    key={competitor.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCompetitors.includes(competitor.id)
                        ? "bg-cyan-950/30 border-cyan-500/30"
                        : "bg-slate-950/50 border-white/5 hover:border-white/10"
                    }`}
                    onClick={() => toggleCompetitor(competitor.id)}
                    data-testid={`select-competitor-${competitor.id}`}
                  >
                    <Checkbox 
                      checked={selectedCompetitors.includes(competitor.id)}
                      className="border-slate-600 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                    />
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{competitor.name}</p>
                      <p className="text-xs text-slate-500 truncate">{competitor.sector}</p>
                    </div>
                    <div className="flex gap-2">
                      {competitor.facebook_url && (
                        <span className="w-5 h-5 rounded bg-blue-500/10 text-blue-400 flex items-center justify-center text-xs font-bold">M</span>
                      )}
                      {competitor.linkedin_url && (
                        <span className="w-5 h-5 rounded bg-sky-500/10 text-sky-400 flex items-center justify-center text-xs font-bold">L</span>
                      )}
                      {competitor.google_ads_domain && (
                        <span className="w-5 h-5 rounded bg-yellow-500/10 text-yellow-400 flex items-center justify-center text-xs font-bold">G</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setSelectDialogOpen(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              onClick={launchAnalysis}
              disabled={selectedCompetitors.length === 0 || launching}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="confirm-launch-btn"
            >
              {launching ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Analizar {selectedCompetitors.length} competidor(es)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Terminal className="w-5 h-5 text-cyan-400" />
              Logs del Análisis
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(logsStatus)}`}>
                {statusLabels[logsStatus] || logsStatus}
              </span>
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Progreso en tiempo real del scraping y análisis
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea data-logs-scroll-area className="h-[400px] mt-4 rounded-lg bg-slate-950 border border-white/5 p-4">
            {logs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Esperando logs...
              </div>
            ) : (
              <div className="space-y-2 font-mono text-sm">
                {logs.map((log, index) => (
                  <LogEntry key={`${log.timestamp || index}-${index}`} log={log} />
                ))}
                <div ref={logsEndRef} />
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="mt-4 sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={copyLogsToClipboard}
              disabled={logs.length === 0}
              className="border-white/10 text-slate-300 hover:bg-white/5"
              data-testid="copy-logs-btn"
            >
              {logsCopied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-emerald-400" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar logs
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setLogsDialogOpen(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quota widget */}
      {quota && (
        <div className="glass rounded-xl p-5 mb-6 flex items-center gap-4" data-testid="quota-widget">
          <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center shrink-0">
            <FileBarChart className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-medium text-slate-300">
                {quota.unlimited ? "Análisis ilimitados" : `Análisis disponibles este período`}
              </h3>
              <span className="text-xs text-slate-500">
                {quota.unlimited
                  ? "Cuenta superadmin"
                  : `Se renueva el ${quota.reset_at ? new Date(quota.reset_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long" }) : "—"}`}
              </span>
            </div>
            {!quota.unlimited && (
              <>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-white">{quota.remaining ?? 0}</span>
                  <span className="text-sm text-slate-500">de {quota.total} restantes</span>
                </div>
                <div className="mt-2 w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full brand-gradient transition-all"
                    style={{ width: `${Math.min(100, (quota.used / Math.max(1, quota.total)) * 100)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Competidores Activos"
          value={stats?.active_competitors || 0}
          total={stats?.total_competitors || 0}
          icon={Users}
          color="cyan"
        />
        <MetricCard
          label="Análisis Realizados"
          value={stats?.total_analyses || 0}
          icon={FileBarChart}
          color="indigo"
        />
        <MetricCard
          label="Anuncios Meta"
          value={stats?.total_ads_meta || 0}
          subValue={`${stats?.winning_ads_meta || 0} ganadores`}
          icon={TrendingUp}
          color="emerald"
        />
        {(stats?.total_ads_linkedin > 0) && (
          <MetricCard
            label="Anuncios LinkedIn"
            value={stats?.total_ads_linkedin || 0}
            subValue={`${stats?.winning_ads_linkedin || 0} ganadores`}
            icon={TrendingUp}
            color="sky"
          />
        )}
        {(stats?.total_ads_google > 0) && (
          <MetricCard
            label="Anuncios Google"
            value={stats?.total_ads_google || 0}
            subValue={`${stats?.winning_ads_google || 0} ganadores`}
            icon={Zap}
            color="yellow"
          />
        )}
      </div>

      {/* Recent Analyses */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Análisis Recientes</h2>
          <Button 
            variant="ghost" 
            className="text-cyan-400 hover:text-cyan-300"
            onClick={() => window.location.href = "/history"}
            data-testid="view-all-analyses-btn"
          >
            Ver todos
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {recentAnalyses.length === 0 ? (
          <div className="empty-state">
            <FileBarChart className="empty-state-icon" />
            <p className="empty-state-title">Sin análisis todavía</p>
            <p className="empty-state-description">
              Agrega competidores y lanza tu primer análisis
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentAnalyses.map((analysis) => (
              <AnalysisRow 
                key={analysis.id} 
                analysis={analysis} 
                onViewLogs={() => openLogsDialog(analysis.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogEntry({ log }) {
  const [expanded, setExpanded] = React.useState(false);
  const levelIcons = {
    info: <Info className="w-4 h-4 text-blue-400" />,
    warning: <AlertTriangle className="w-4 h-4 text-yellow-400" />,
    error: <AlertCircle className="w-4 h-4 text-red-400" />,
    success: <CheckCircle2 className="w-4 h-4 text-emerald-400" />
  };
  
  const levelColors = {
    info: "text-slate-300",
    warning: "text-yellow-300",
    error: "text-red-300",
    success: "text-emerald-300"
  };
  
  const time = new Date(log.timestamp).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });

  const hasDetails = log.details && Object.keys(log.details).length > 0;
  
  return (
    <div className={`${levelColors[log.level] || "text-slate-300"}`}>
      <div className="flex items-start gap-2">
        <span className="flex-shrink-0 mt-0.5">{levelIcons[log.level] || levelIcons.info}</span>
        <span className="text-slate-500 flex-shrink-0">[{time}]</span>
        <span className="flex-1">{log.message}</span>
        {hasDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-cyan-400 hover:text-cyan-300 flex-shrink-0 ml-2"
            data-testid="log-details-toggle"
          >
            {expanded ? "Ocultar" : "Ver detalles"}
          </button>
        )}
      </div>
      {expanded && hasDetails && (
        <pre
          className="mt-1 ml-6 p-2 bg-slate-900/80 rounded text-xs text-slate-400 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all"
          data-testid="log-details-content"
        >
          {log.details.json_chunk || JSON.stringify(log.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

function MetricCard({ label, value, total, subValue, icon: Icon, color }) {
  const colorClasses = {
    cyan: "text-cyan-400",
    indigo: "text-indigo-400",
    emerald: "text-emerald-400",
    purple: "text-purple-400"
  };

  return (
    <div className="metric-card">
      <Icon className={`metric-icon w-6 h-6 ${colorClasses[color]}`} strokeWidth={1.5} />
      <div className="metric-value">{value.toLocaleString()}</div>
      <div className="metric-label">
        {label}
        {total !== undefined && ` / ${total}`}
      </div>
      {subValue && (
        <div className={`text-xs mt-1 ${colorClasses[color]}`}>{subValue}</div>
      )}
    </div>
  );
}

function AnalysisRow({ analysis, onViewLogs }) {
  const date = new Date(analysis.created_at).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });

  const isInProgress = ["pending", "scraping", "analyzing"].includes(analysis.status);

  return (
    <div 
      className="flex items-center gap-4 p-4 rounded-lg bg-slate-900/50 border border-white/5 hover:border-cyan-500/20 transition-colors"
      data-testid={`analysis-row-${analysis.id}`}
    >
      <div className="flex-shrink-0">
        {getStatusIcon(analysis.status)}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`status-badge ${analysis.status}`}>
            {statusLabels[analysis.status]}
          </span>
          <span className="text-sm text-slate-500">
            {analysis.competitors_ids?.length || 0} competidores
          </span>
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {date}
          </span>
          {analysis.status === "completed" && (
            <>
              {analysis.total_ads_meta > 0 && <span>Meta: {analysis.total_ads_meta}</span>}
              {analysis.total_ads_linkedin > 0 && <span>LinkedIn: {analysis.total_ads_linkedin}</span>}
              {analysis.total_ads_google > 0 && <span>Google: {analysis.total_ads_google}</span>}
            </>
          )}
          {analysis.error_message && (
            <span className="text-red-400 truncate max-w-[200px]" title={analysis.error_message}>
              {analysis.error_message}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* View Logs Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewLogs}
          className="text-slate-400 hover:text-cyan-400"
          data-testid={`view-logs-${analysis.id}`}
        >
          <Terminal className="w-4 h-4" />
        </Button>
        
        {analysis.status === "completed" && (
          <div className="flex gap-2">
            {analysis.report_url && (
              <a
                href={analysis.report_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                data-testid={`report-link-${analysis.id}`}
              >
                Informe <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>

      {isInProgress && (
        <div className="w-32">
          <Progress value={PROGRESS_BY_STATUS[analysis.status] ?? 0} className="h-1" />
        </div>
      )}
    </div>
  );
}
